import React, { useState, useEffect } from 'react';
import api from "../../../api/api";
import AdminPanel from '../panel/AdminPanel';
import { FaShoppingCart, FaBoxOpen, FaTags, FaUsers, FaDollarSign, FaTruck } from 'react-icons/fa';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import '../styles/EcommercePage.css';


const EcommerceStoreManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [barYear, setBarYear] = useState(new Date().getFullYear());
  const [pieYear, setPieYear] = useState(new Date().getFullYear());

  const [products, setProducts] = useState([]);
  const [revenueByYear, setRevenueByYear] = useState({});
  const [pieByYear, setPieByYear] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', price: '', stock: '' });

  const COLORS = ['#03a9f4', '#ff9800', '#f44336', '#8e24aa', '#4caf50'];
 


// Fetch products + revenue data
useEffect(() => {
  api.get("/api/products")
    .then(res => setProducts(Array.isArray(res.data) ? res.data : []))
    .catch(err => {
      console.error("Error fetching products:", err);
      setProducts([]);
    });

  api.get("/api/revenue")
    .then(res => setRevenueByYear(res.data || {}))
    .catch(err => {
      console.error("Error fetching revenue:", err);
      setRevenueByYear({});
    });

  api.get("/api/revenue/breakdown")
    .then(res => setPieByYear(res.data || {}))
    .catch(err => {
      console.error("Error fetching breakdown:", err);
      setPieByYear({});
    });
}, []);

// Extract available years dynamically
const years = Object.keys(revenueByYear).map(y => parseInt(y, 10));

// Add product
const handleAddProduct = () => {
 api.post("/api/products", {
  name: newProduct.name,
  price: newProduct.price,
  stock: newProduct.stock
}).then(res => {
    setProducts(prev => [...prev, { ...res.data, media: [] }]);
    setNewProduct({ name: '', price: '', stock: '' });
    setProductModalOpen(false);
  }).catch(err => console.error("Error adding product:", err));
};

// Delete product
const handleDeleteProduct = (id) => {
  api.delete(`/api/products/${id}`)
    .then(() => {
      setProducts(prev => prev.filter(p => p.id !== id));
    })
    .catch(err => console.error("Error deleting product:", err));
};
// Add media
const addMedia = (productId, file) => {
  const formData = new FormData();
  formData.append("file", file);

  api.post(`/api/products/${productId}/media`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  })
  .then(res => {
    setProducts(prev =>
      prev.map(p =>
        p.id === productId ? { ...p, media: res.data.media } : p
      )
    );
  })
  .catch(err => console.error("Error adding media:", err));
};

// Delete media
const deleteMedia = (productId, mediaId) => {
  api.delete(`/api/products/${productId}/media/${mediaId}`)
    .then(() => {
      setProducts(products.map(p => {
        if (p.id === productId) {
          return { ...p, media: p.media.filter(m => m.id !== mediaId) };
        }
        return p;
      }));
    }).catch(err => console.error("Error deleting media:", err));
};

const openMediaModal = (index) => {
  setCurrentProduct(index);
  setModalOpen(true);
};

  return (
    <div className="ecommerce-page">
      <AdminPanel onToggle={setIsSidebarCollapsed} />

      <div className={`ecommerce-main-content ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <h1>Ecommerce Store Management</h1>

        {/* Quick Links Grid */}
        <div className="ecommerce-quick-links-grid">
          <div className="ecommerce-quick-card products">
            <FaBoxOpen className="ecommerce-card-icon" />
            <div className="ecommerce-card-info">
              <span className="ecommerce-card-value">{products.length}</span>
              <span className="ecommerce-card-label">Products</span>
            </div>
          </div>
          <div className="ecommerce-quick-card orders">
            <FaShoppingCart className="ecommerce-card-icon" />
            <div className="ecommerce-card-info">
              <span className="ecommerce-card-value">15</span>
              <span className="ecommerce-card-label">Orders</span>
            </div>
          </div>
          <div className="ecommerce-quick-card customers">
            <FaUsers className="ecommerce-card-icon" />
            <div className="ecommerce-card-info">
              <span className="ecommerce-card-value">25</span>
              <span className="ecommerce-card-label">Customers</span>
            </div>
          </div>
          <div className="ecommerce-quick-card revenue">
            <FaDollarSign className="ecommerce-card-icon" />
            <div className="ecommerce-card-info">
              <span className="ecommerce-card-value">₹ 12,500</span>
              <span className="ecommerce-card-label">Revenue</span>
            </div>
          </div>
          <div className="ecommerce-quick-card discounts">
            <FaTags className="ecommerce-card-icon" />
            <div className="ecommerce-card-info">
              <span className="ecommerce-card-value">3</span>
              <span className="ecommerce-card-label">Active Discounts</span>
            </div>
          </div>
          <div className="ecommerce-quick-card shipments">
            <FaTruck className="ecommerce-card-icon" />
            <div className="ecommerce-card-info">
              <span className="ecommerce-card-value">5</span>
              <span className="ecommerce-card-label">Pending Shipments</span>
            </div>
          </div>
        </div>

        {/* Product Table */}
        <div className="ecommerce-table-section">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Price (₹)</th>
                <th>Stock</th>
                <th>Action</th>
                <th>Media</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, index) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.price}</td>
                  <td>{p.stock}</td>
                  <td>
                    <button>Edit</button>
                    <button onClick={() => handleDeleteProduct(p.id)}>Delete</button>
                  </td>
                  <td>
                    <button onClick={() => openMediaModal(index)}>Manage Media</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="add-product-btn" onClick={() => setProductModalOpen(true)}>Add New Product</button>
        </div>

        {/* Add Product Modal */}
        {productModalOpen && (
          <div className="media-card-overlay">
            <div className="media-card">
              <h3>Add New Product</h3>
              <div className="add-product-form">
                <label>Product Name:</label>
                <input type="text" value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} />

                <label>Price (₹):</label>
                <input type="number" value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: Number(e.target.value) })} />

                <label>Stock:</label>
                <input type="number" value={newProduct.stock} onChange={(e) => setNewProduct({ ...newProduct, stock: Number(e.target.value) })} />

                <div className="add-product-buttons">
                  <button onClick={handleAddProduct}>Add Product</button>
                  <button onClick={() => setProductModalOpen(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Media Modal */}
        {modalOpen && currentProduct !== null && (
          <div className="media-card-overlay">
            <div className="media-card">
              <h3>Manage Media for {products[currentProduct].name}</h3>

              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="addImageInput"
                onChange={(e) => {
                  if (e.target.files[0]) addMedia(products[currentProduct].id, e.target.files[0]);
                }}
              />
              <button onClick={() => document.getElementById('addImageInput').click()}>Add Image</button>

              <input
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                id="addVideoInput"
                onChange={(e) => {
                  if (e.target.files[0]) addMedia(products[currentProduct].id, e.target.files[0]);
                }}
              />
              <button onClick={() => document.getElementById('addVideoInput').click()}>Add Video</button>

              <div className="media-list">
                {products[currentProduct].media.map((m, i) => (
                  <div key={m.id || i} className="media-item">
                    {m.url.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                      <>
                        <img src={m.url} alt="" width={100} />
                        <button className="delete-media-btn" onClick={() => deleteMedia(products[currentProduct].id, m.id)}>Delete Image</button>
                      </>
                    ) : (
                      <>
                        <video src={m.url} width={150} controls />
                        <button className="delete-media-btn" onClick={() => deleteMedia(products[currentProduct].id, m.id)}>Delete Video</button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <button className="close-media-card" onClick={() => setModalOpen(false)}>Close</button>
            </div>
          </div>
        )}

        {/* Bar Chart */}
        <div className="ecommerce-year-selector">
          <label>Select Year for Monthly Revenue: </label>
          <select value={barYear} onChange={(e) => setBarYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {revenueByYear[barYear] && (
          <div className="ecommerce-revenue-chart">
            <h3>Monthly Revenue ({barYear})</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueByYear[barYear]} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `₹ ${value.toLocaleString()}`} />
                <Legend />
                <Bar dataKey="revenue" name="Revenue" fill="#03a9f4" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Pie Chart */}
        <div className="ecommerce-year-selector">
          <label>Select Year for Revenue Breakdown: </label>
          <select value={pieYear} onChange={(e) => setPieYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {pieByYear[pieYear] && (
          <div className="ecommerce-revenue-pie-chart">
            <h3>Revenue Breakdown ({pieYear})</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieByYear[pieYear]}
                  dataKey="value"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.category}: ₹${entry.value.toLocaleString()}`}
                >
                  {pieByYear[pieYear].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `₹ ${value.toLocaleString()}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default EcommerceStoreManagement;
